import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens, Text, Loader, Icon, Link, Button } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { VerificationStatus } from '@hotjar/webapp/domains/activation/tracking-code/types';
import { useTrackingCodeVerification } from '@hotjar/webapp/hooks/useTrackingCodeVerification';
import { usePrevious } from 'react-use';
import { useHelpHotjarUrl } from '@hotjar/translations';

interface TrackingCodeVerificationResultProps {
  url: string;
  site: Site;
  onLoad: (status: VerificationStatus) => void;
  onErrorButtonClick: () => void;
  onSuccessButtonClick: () => void;
}

const Wrapper = styled.div`
  ${tokens.flexCenter()};
  flex-direction: column;
`;

const StyledLoader = styled(Loader)`
  margin: ${tokens.spacer.x3} 0;
`;

const StyledButton = styled(Button)`
  margin: ${tokens.spacer.x3} 0 0;
`;

const StyledIcon = styled(Icon)`
  margin: 0 0 ${tokens.spacer.x3};
`;

const LoadingState = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Wrapper data-testid="tracking-code-verification-result">
      <StyledLoader size="xl" />
      <Text as="p" textType="subtitle1">
        {t('activation:tracking-code.verification.pending.title')}
      </Text>
      <Text as="p" textColor="text_light">
        {t('activation:tracking-code.verification.pending.caption')}
      </Text>
    </Wrapper>
  );
};

interface SuccessStateProps {
  warning?: string;
  onClick: () => void;
}

const SuccessState = ({ warning, onClick }: SuccessStateProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Wrapper data-testid="tracking-code-verification-result">
      <StyledIcon icon="check" color="text_success" size="xl" />
      <Text as="p" textType="subtitle1" textColor="text_success">
        {t('activation:tracking-code.verification.success.title')}
      </Text>
      <Text as="p" textColor="text_light">
        {t('activation:tracking-code.verification.success.caption')}
      </Text>
      {warning ? (
        <Text as="p" textType="caption" textColor="text_warning">
          {warning}
        </Text>
      ) : null}
      <Button onClick={onClick}>{t('activation:tracking-code.verification.continue')}</Button>
    </Wrapper>
  );
};

interface ErrorStateProps {
  title: string;
  caption: string;
  onClick: () => void;
}

const ErrorState = ({ title, caption, onClick }: ErrorStateProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();

  return (
    <Wrapper data-testid="tracking-code-verification-result">
      <StyledIcon icon="error" color="text_error" size="xl" />
      <Text as="p" textType="subtitle1" textColor="text_error">
        {title}
      </Text>
      <Text as="p" textColor="text_light">
        {caption}
      </Text>
      <Link
        kind="standalone"
        href={`${helpHotjarUrl}/articles/115009336727?itm_source=insights_installation_help`}
        isExternal
      >
        {t('activation:tracking-code.verification.need-help')}
      </Link>
      <StyledButton onClick={onClick}>
        {t('activation:tracking-code.verification.try-again')}
      </StyledButton>
    </Wrapper>
  );
};

export const TrackingCodeVerificationResult = ({
  url,
  site,
  onErrorButtonClick,
  onSuccessButtonClick,
  onLoad,
}: TrackingCodeVerificationResultProps): JSX.Element | null => {
  const { t } = useTranslation();
  const status = useTrackingCodeVerification(url, site);
  const previousStatus = usePrevious(status);

  useEffect(() => {
    if (previousStatus === VerificationStatus.PENDING && status !== VerificationStatus.PENDING) {
      onLoad(status);
    }
  }, [status, onLoad, previousStatus]);

  switch (status) {
    case VerificationStatus.PENDING:
      return <LoadingState />;
    case VerificationStatus.SUCCESS:
      return <SuccessState onClick={onSuccessButtonClick} />;
    case VerificationStatus.WARNING:
      return (
        <SuccessState
          onClick={onSuccessButtonClick}
          warning={t('activation:tracking-code.verification.success.warning')}
        />
      );
    case VerificationStatus.WRONG_CODE:
      return (
        <ErrorState
          onClick={onErrorButtonClick}
          title={t('activation:tracking-code.verification.wrong-code.title')}
          caption={t('activation:tracking-code.verification.wrong-code.caption')}
        />
      );
    case VerificationStatus.MULTIPLE_CODES:
      return (
        <ErrorState
          onClick={onErrorButtonClick}
          title={t('activation:tracking-code.verification.multiple-codes.title')}
          caption={t('activation:tracking-code.verification.multiple-codes.caption')}
        />
      );
    case VerificationStatus.DNS_FAILURE:
    case VerificationStatus.CONNECTION_FAILED:
    case VerificationStatus.UNDEFINED_FAILURE:
    case VerificationStatus.TIMEOUT:
      return (
        <ErrorState
          onClick={onErrorButtonClick}
          title={t('activation:tracking-code.verification.code-not-found.title')}
          caption={t('activation:tracking-code.verification.code-not-found.caption')}
        />
      );
    default:
      return null;
  }
};
